import React, { useState, Fragment, useCallback } from 'react';
import { array, object } from 'prop-types';
import { Link } from "@reach/router";
import { linkTo } from 'utils/links';
import { Trans } from '@lingui/macro';
import { renderIcon } from 'utils/menu';
import classNames from 'classnames';
import Config from 'config';

// Components
import Button from 'components/Button';

// Icons
import Logo from 'theme/images/logo.svg';
import MusicIcon from 'components/Icon/Music';
import AnnouncementIcon from 'components/Icon/Announcement';
import CurrencyDollarIcon from 'components/Icon/CurrencyDollar';
import Speaker from 'components/Icon/Speaker';
import GraphBar from 'components/Icon/GraphBar';
import Heart from 'components/Icon/Heart';
import Clock from 'components/Icon/Clock';
import CogIcon from 'components/Icon/Cog';
import ChatIcon from 'components/Icon/Chat';
import GraphBarIcon from 'components/Icon/GraphBar';
import GroupIcon from 'components/Icon/Group';
import NewsIcon from 'components/Icon/News';
import Folder from 'components/Icon/Folder';
import TagIcon from 'components/Icon/Tag';
import Menu from 'components/Icon/Menu';
import CheveronDownIcon from 'components/Icon/CheveronDown';
import CheveronUpIcon from 'components/Icon/CheveronUp';
import Search from 'components/Icon/Search';
import Microphone from 'components/Icon/Microphone';
import Chat from 'components/Icon/Chat';

import './styles.css';

export default function SideNav({ user, onToggleSearch, menu }) {
  const [isOpen, toggle] = useState(false);

  return (
    <Fragment>
      <Button onClick={() => toggle(true)} type="plain" className="fixed mt-4 px-3 py-1 text-secondary md:mt-8 md:ml-4 md:py-2 xl:hidden xl:mt-4">
        <Menu width={22} height={22} className="fill-current" />
      </Button>
      {isOpen && <div onClick={() => toggle(false)} className="fixed z-40 inset-0 bg-black-lighter md:z-10" />}
      <nav
        className={classNames('SideNav main-sidenav fixed z-50 bg-page h-full shadow lg:ml-0 lg:static md:z-10', {
          '-ml-sidebar': !isOpen,
        })}
        data-test="sidenav"
      >
        <div className="bg-main-menu flex flex-col h-full">
          <h1 className="SideNav_title pl-8 text-default text-xl pt-4 uppercase flex lg:pl-4 xl:pl-8">
            {!Config.app.APP_LOGO &&
              <Fragment>
                <img src={Logo} className="SideNav_logo_icon block mr-3" /> <span className="block font-sans lg:hidden xl:block">{Config.app.APP_TITLE}</span>
              </Fragment>
            }
            {Config.app.APP_LOGO &&
              <img src={Config.app.APP_LOGO} className="SideNav_logo_icon block mr-3" />
            }
          </h1>
          <div className="pl-8 pt-12 pr-2 flex-1 overflow-auto scrolling-touch lg:pl-1 xl:pl-8" onClick={() => toggle(false)}>
            {user.admin && <AdminMenu />}
            {user.author && <AuthorMenu />}
            <MainMenu user={user} menu={menu} onToggleSearch={onToggleSearch} />
          </div>
          <div className="pl-8" style={{ height: '50px' }} />
        </div>
      </nav>
    </Fragment>
  );
}

SideNav.propTypes = {
  user: object,
  menu: array,
};

SideNav.defaultProps = {
  user: {},
  menu: [],
};

function AdminMenu() {
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded(!expanded), [expanded]);
  return (
    <ul data-test="menu-admin" className="list-reset pb-4">
      <li onClick={toggle} className="overflow-hidden pb-4 text-xs text-default font-sans uppercase cursor-pointer lg:text-center lg:text-xxs xl:text-left xl:text-xs">
        <Trans>Admin</Trans>
        {expanded && <CheveronUpIcon className="fill-current inline-block" width={16} height={16} />}
        {!expanded && <CheveronDownIcon className="fill-current inline-block" width={16} height={16} />}
      </li>
      {expanded &&
        <Fragment>
          <li><MenuItem url={linkTo('/admin/dashboard')} Icon={GraphBarIcon}><Trans>Dashboard</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/admin/billing')} Icon={CurrencyDollarIcon}><Trans>Billing</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/admin/albums')} Icon={MusicIcon}><Trans>Albums</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/admin/singles')} Icon={Speaker}><Trans>Singles</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/admin/podcasts')} Icon={Microphone}><Trans>Podcasts</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/admin/ads')} Icon={AnnouncementIcon}><Trans>Ad Manager</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/admin/blog')} Icon={NewsIcon}><Trans>Blog</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/admin/comments')} Icon={ChatIcon}><Trans>Comments</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/admin/genres')} Icon={TagIcon}><Trans>Genres</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/admin/users')} Icon={GroupIcon}><Trans>Users</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/admin/config')} Icon={CogIcon}><Trans>Configurations</Trans></MenuItem></li>
        </Fragment>
      }
    </ul>
  );
}

function AuthorMenu() {
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded(!expanded), [expanded]);

  return (
    <ul data-test="menu-author" className="list-reset pb-4">
      <li onClick={toggle} className="overflow-hidden pb-4 text-xs text-default font-sans uppercase cursor-pointer lg:text-center lg:text-xxs xl:text-left xl:text-xs">
        <Trans>Authors</Trans>
        {expanded && <CheveronUpIcon className="fill-current inline-block" width={16} height={16} />}
        {!expanded && <CheveronDownIcon className="fill-current inline-block" width={16} height={16} />}
      </li>
      {expanded &&
        <Fragment>
          <li><MenuItem url={linkTo('/authors/albums')} Icon={MusicIcon}><Trans>My Albums</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/authors/singles')} Icon={Speaker}><Trans>My Singles</Trans></MenuItem></li>
          <li><MenuItem url={linkTo('/authors/podcasts')} Icon={Microphone}><Trans>My Podcasts</Trans></MenuItem></li>
          {Config.app.APP_AUTHORS_PUBLISH_POSTS === 'Enabled' &&
            <li><MenuItem url={linkTo('/authors/blog')} Icon={NewsIcon}><Trans>My Posts</Trans></MenuItem></li>
          }
        </Fragment>
      }
    </ul>
  );
}

function MainMenu({ menu, onToggleSearch }) {
  return (
    <Fragment>
      <ul data-test="menu-public" className="list-reset pb-4">
        <li className="overflow-hidden pb-4 text-xs text-default font-sans uppercase cursor-pointer lg:text-center lg:text-xxs xl:text-left xl:text-xs">
          <Trans>Explore Music</Trans>
        </li>
        <li><MenuItem url={linkTo('/search')} Icon={Search} onClick={onToggleSearch}><Trans>Search</Trans></MenuItem></li>
        {menu.length === 0 &&
          <Fragment>
            <li><MenuItem url={linkTo('/discover')} Icon={MusicIcon}><Trans>Discover</Trans></MenuItem></li>
            <li><MenuItem url={linkTo('/browse')} Icon={GraphBar}><Trans>Browse</Trans></MenuItem></li>
            <li><MenuItem url={linkTo('/forum/all')} Icon={Chat}><Trans>Forum</Trans></MenuItem></li>
            <li><MenuItem url={linkTo('/blog')} Icon={NewsIcon}><Trans>News</Trans></MenuItem></li>
            <li><MenuItem url={linkTo('/artists')} Icon={GroupIcon}><Trans>Artists</Trans></MenuItem></li>
          </Fragment>
        }
        {menu.map((option) =>
          <li key={option.id}><MenuDynamicOption option={option} /></li>
        )}
      </ul>
      <ul data-test="menu-member" className="list-reset mt-4">
        <li className="overflow-hidden pb-4 text-xs text-default font-sans uppercase cursor-pointer lg:text-center lg:text-xxs xl:text-left xl:text-xs">
          <Trans>Your Music</Trans>
        </li>
        <li><MenuItem url={linkTo('/members/favorites')} Icon={Heart}><Trans>Favorites</Trans></MenuItem></li>
        <li><MenuItem url={linkTo('/members/history')} Icon={Clock}><Trans>History</Trans></MenuItem></li>
        <li><MenuItem url={linkTo('/members/playlists')} Icon={Folder}><Trans>Playlists</Trans></MenuItem></li>
      </ul>
    </Fragment>
  );
}

function MenuItem({ children, Icon, url, onClick }) {
  const Comp = onClick ? 'button' : Link;
  const baseCss = 'SideNav__MenuItem cursor-pointer pr-4 py-2 block flex text-sm hover:text-default';
  const props = {};

  if (onClick) {
    props.className = `${baseCss} text-secondary focus:outline-none`;
    props.onClick = onClick;
  } else {
    props.to = url;
    props.getProps = ({ isCurrent, isPartiallyCurrent }) => {
      return {
        className: (isCurrent || isPartiallyCurrent) ? `${baseCss} text-default SideNav__MenuItem__active` : `${baseCss} text-secondary`,
      };
    }
  }

  return (
    <Comp {...props}>
      <div className="SideNav__MenuItem__selected block border-l-4 border-primary pl-2" />
      {Icon && <Icon className="SideNav__MenuItem__icon fill-current block" />}
      <span className="block pl-4 font-sans font-light lg:hidden xl:inline">{children}</span>
    </Comp>
  );
}
const isExternal = /^http(s)?/;
function MenuDynamicOption({ option }) {
  const external = isExternal.test(option.link);
  const Comp = external ? 'a' : Link;
  const baseCss = 'SideNav__MenuItem cursor-pointer pr-4 py-2 block flex text-sm hover:text-default';
  const props = {};

  if (external) {
    props.className = `${baseCss} text-secondary focus:outline-none`;
    props.href = option.link;
  } else {
    props.to = option.link;
    props.getProps = ({ isCurrent, isPartiallyCurrent }) => {
      return {
        className: (isCurrent || isPartiallyCurrent) ? `${baseCss} text-default SideNav__MenuItem__active` : `${baseCss} text-secondary`,
      };
    }
  }

  if (!option.visible) {
    return <div />;
  }

  return (
    <Comp {...props}>
      <div className="SideNav__MenuItem__selected block border-l-4 border-primary pl-2" />
      {renderIcon(option)}
      <span className="block pl-4 font-sans font-light lg:hidden xl:inline">{option.label}</span>
    </Comp>
  );
}
